import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MessageSquareIcon, LayoutIcon, PencilIcon, CheckCircleIcon, RocketIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function SaleWhyUs() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t,
    language
  } = useLanguage();
  const getStep4Text = () => {
    if (language === 'ru') {
      return {
        title: 'Если всё подходит — вы оплачиваете хостинг',
        note: 'Оплата только после того, как вы скажете: "Да, мне подходит". Вы оплачиваете только хостинг, сайт будет стоить всего €15 в месяц.'
      };
    } else if (language === 'ua') {
      return {
        title: 'Якщо все підходить — ви сплачуєте хостинг',
        note: 'Оплата тільки після того, як ви скажете: "Так, мені підходить". Ви оплачуєте лише хостинг, сайт буде коштувати всього €15 на місяць.'
      };
    } else {
      return {
        title: 'If everything suits you — you pay for hosting',
        note: 'Payment only after you say: "Yes, this works for me". You only pay for hosting, the website will cost just €15 per month.'
      };
    }
  };
  const step4 = getStep4Text();
  const steps = [{
    icon: MessageSquareIcon,
    title: t('how.step1.title'),
    note: t('how.step1.note'),
    number: '01'
  }, {
    icon: LayoutIcon,
    title: t('how.step2.title'),
    note: t('how.step2.note'),
    number: '02'
  }, {
    icon: PencilIcon,
    title: t('how.step3.title'),
    note: t('how.step3.note'),
    number: '03'
  }, {
    icon: CheckCircleIcon,
    title: step4.title,
    note: step4.note,
    number: '04'
  }, {
    icon: RocketIcon,
    title: t('how.step5.title'),
    note: t('how.step5.note'),
    number: '05'
  }];
  return <section id="how-it-works" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-285" data-mp="29jn5jx3ijilo">
      <div className="relative z-10 mx-auto max-w-6xl" data-id="element-286" data-mp="2lkdf2mcgy63e">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-12 sm:mb-16 text-center" data-id="element-287" data-mp="2lmzl2hjpodji">
          <h2 className="mb-4 text-3xl font-bold md:text-4xl" style={{
          color: 'var(--text-primary)'
        }} data-id="element-288" data-mp="22uwykpke231c">
            {t('how.title')} {t('how.titleHighlight')}
          </h2>
        </motion.div>

        <div className="relative flex flex-col gap-8 sm:gap-12" data-id="element-289" data-mp="1f5ulzw4m30dp">
          {steps.map((step, index) => {
          const isRight = index % 2 === 0;
          return <motion.div key={index} initial={{
            opacity: 0,
            x: isRight ? 30 : -30,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            x: 0,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2 + index * 0.12,
            type: 'spring',
            stiffness: 80
          }} className={`relative ${isRight ? 'sm:ml-auto sm:mr-0' : 'sm:mr-auto sm:ml-0'} sm:w-[85%]`} data-id="element-290" data-mp="1ckmwur59p71p" data-mpi={index}>
                <div className="rounded-3xl border p-6 sm:p-8 relative overflow-hidden" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-291" data-mp="eyfpj9hkx0ln">
                  <div className="absolute top-4 right-6 text-7xl sm:text-8xl font-bold opacity-[0.03]" style={{
                color: 'var(--text-primary)'
              }} data-id="element-292" data-mp="unvmsd3wsuin">
                    {step.number}
                  </div>
                  <div className="flex gap-4 sm:gap-6 relative z-10" data-id="element-293" data-mp="put08oxjulby">
                    <motion.div initial={{
                  scale: 0
                }} animate={isInView ? {
                  scale: 1
                } : {}} transition={{
                  duration: 0.4,
                  delay: 0.4 + index * 0.12,
                  type: 'spring'
                }} className="flex-shrink-0" data-id="element-294" data-mp="bvtcdjhv2agu">
                      <div className="flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-2xl" style={{
                    backgroundColor: 'var(--bg-tertiary)'
                  }} data-id="element-295" data-mp="3lsjv1lftqd5d">
                        <step.icon className="h-7 w-7 sm:h-8 sm:w-8" style={{
                      color: 'var(--accent)'
                    }} data-id="element-296" data-mp="3qso974t9evsf" />
                      </div>
                    </motion.div>
                    <div className="flex-1 pt-1" data-id="element-297" data-mp="iccp189xs3tf">
                      <h3 className="mb-2 text-lg sm:text-xl font-bold leading-tight" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-298" data-mp="ic30lp4ji4pu">
                        {step.title}
                      </h3>
                      <p className="text-sm sm:text-base" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-299" data-mp="2f6zffefqzo66">
                        {step.note}
                      </p>
                    </div>
                  </div>
                  <motion.div initial={{
                scale: 0
              }} animate={isInView ? {
                scale: [0, 1.3, 1]
              } : {}} transition={{
                duration: 0.5,
                delay: 0.5 + index * 0.12
              }} className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${isRight ? '-left-1.5 sm:-left-6' : '-right-1.5 sm:-right-6'} hidden sm:block`} style={{
                backgroundColor: 'var(--accent)'
              }} data-id="element-300" data-mp="x19ktol80hy5" />
                </div>
              </motion.div>;
        })}
        </div>
        <motion.p initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        delay: 1.2
      }} className="text-center mt-12 text-sm sm:text-base" style={{
        color: 'var(--text-secondary)'
      }} data-id="element-301" data-mp="357tg1re4nhds">
          {t('how.note')}
        </motion.p>
      </div>
    </section>;
}